import React, { useEffect, useMemo, useState } from 'react';
import Dialog from 'components/Dialog/Dialog';
import DialogContent from 'components/Dialog/DialogContent';
import DialogTitle from 'components/Dialog/DialogTitle';
import DialogActions from 'components/Dialog/DialogActions';
import Grid from 'components/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { chatActions, chatSelectors, chatTypes } from 'redux/chat';
import { workspaceSelectors } from 'redux/workspace';
import Avatar from 'components/Avatar';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Form from 'components/FinalForm/Form';
import TrashIcon from 'components/Icons/Trash';
import IconButton from '@mui/material/IconButton';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MemberAutocomplete from 'containers/Fields/MemberAutocomplete';
import PropTypes from 'prop-types';
import MemberJoinStatus, { isNotJoined, renderMemberJoinStatus } from './MemberJoinStatus';
import useConversableMembers from './useConversableMembers';

const Name = styled('div')`
  font-weight: 500;
`;

const Job = styled('span')`
  color: #666;
  font-size: 0.9em;
`;

const SecondaryLine = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MemberCount = styled('span')`
  color: #999;
  font-weight: 400;
`;

const MemberListItem = ({ conversationId, member, canDelete, ...rest }) => {

  const dispatch = useDispatch();

  const onDelete = () => {
    dispatch(chatActions.conversationMemberDelete({ conversationId, memberId: member.id }));
  };

  if (!member) {
    return null;
  }

  return (
    <ListItem disableGutters {...rest}>
      <Grid container direction="row" spacing={1} wrap="nowrap" alignItems="center">
        <Grid item>
          <Avatar size={36} src={member.avatar?.src} srcSet={member.avatar?.srcSet} firstName={member.firstName} lastName={member.lastName} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <ListItemText
            primary={<Name>{member.name}</Name>}
            secondary={(
              <SecondaryLine>
                <Job>{member.job}</Job>
                <MemberJoinStatus member={member} />
              </SecondaryLine>
            )}
            secondaryTypographyProps={{ component: 'div' }}
          />
        </Grid>
        <Grid item>
          {canDelete && (
            <IconButton size="small" onClick={onDelete}>
              <TrashIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </ListItem>
  );
};

MemberListItem.propTypes = {
  conversationId: PropTypes.any.isRequired,
  member: PropTypes.shape({
    id: PropTypes.any,
    avatar: PropTypes.any,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    job: PropTypes.string,
  }),
  canDelete: PropTypes.bool,
};

const AddMembersForm = ({ conversationId, existingMemberIds, onCancel, onAdded }) => {

  const { t } = useTranslation('chat');

  const dispatch = useDispatch();

  const { options, loading: optionsLoading } = useConversableMembers();

  const addableOptions = useMemo(() => {
    const existing = new Set(existingMemberIds.map(String));

    return options.filter(memberId => !existing.has(String(memberId)));
  }, [existingMemberIds, options]);

  const initialValues = useMemo(() => ({ memberIds: [], conversationId }), [conversationId]);

  const onSuccess = () => {
    dispatch(chatActions.getMembersForConversation({ conversationId }));
    onAdded();
  };

  return (
    <Form
      start={chatTypes.conversationMemberAdd}
      resolve={chatTypes.conversationMemberAddSuccess}
      reject={chatTypes.conversationMemberAddFailure}
      onSuccess={onSuccess}
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting, values }) => (
        <>
          <DialogContent>
            <MemberAutocomplete.FinalForm
              multiple
              name="memberIds"
              placeholder={t('chat:manageMembers.add.membersPlaceholder')}

              options={addableOptions}
              loading={optionsLoading}
              renderMemberSuffix={renderMemberJoinStatus}
              showBelowDivider={isNotJoined}

              disableCloseOnSelect

              helperText={t('chat:manageMembers.add.historyVisibleDisclaimer')}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onCancel}>
              {t('chat:manageMembers.add.cancelButton')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !values.memberIds?.length}>
              {t('chat:manageMembers.add.addButton')}
            </Button>
          </DialogActions>
        </>
      )}
    </Form>
  );
};

AddMembersForm.propTypes = {
  conversationId: PropTypes.any.isRequired,
  existingMemberIds: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onAdded: PropTypes.func.isRequired,
};

const ManageMembersDialog = ({ conversationId, onClose, ...rest }) => {

  const dispatch = useDispatch();
  const { t } = useTranslation('chat');

  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    dispatch(chatActions.getMembersForConversation({ conversationId }));
  }, [conversationId, dispatch]);

  const conversation = useSelector(chatSelectors.getConversationSelector)(conversationId);

  const memberIds = useSelector(chatSelectors.getMemberIdsForConversation)(conversationId);

  const getMemberSelector = useSelector(workspaceSelectors.getMemberSelector);

  const canManageMembers = !!conversation?.permissions?.manageMembers;

  // TODO: Translations etc.
  if (conversation?.permissions?.manageMembers === false) {
    return (
      <Dialog maxWidth="xs" fullWidth onClose={onClose} {...rest}>
        <DialogTitle>
          {t('chat:manageMembers.title')}
        </DialogTitle>
        <DialogContent>
          {t('chat:manageMembers.notAllowed')}
        </DialogContent>
      </Dialog>
    );
  }

  const members = memberIds.map(getMemberSelector).filter(v => !!v);
  const stopAdding = () => setIsAdding(false);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={onClose} {...rest}>
      {isAdding ? (
        <>
          <DialogTitle>{t('chat:manageMembers.add.membersLabel')}</DialogTitle>
          <AddMembersForm
            conversationId={conversationId}
            existingMemberIds={memberIds}
            onCancel={stopAdding}
            onAdded={stopAdding}
          />
        </>
      ) : (
        <>
          <DialogTitle>
            <Grid container spacing={2} justifyContent="space-between" alignItems="center" wrap="nowrap">
              <Grid item>
                {t('chat:manageMembers.title')}
                {' '}
                <MemberCount>{`· ${members.length}`}</MemberCount>
              </Grid>
              {canManageMembers && (
                <Grid item>
                  <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setIsAdding(true)}>
                    {t('chat:manageMembers.add.addButton')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </DialogTitle>
          <DialogContent>
            <List disablePadding>
              {members.map((member, idx, arr) => (
                <MemberListItem
                  key={member.id}
                  member={member}
                  divider={arr.length - 1 !== idx}

                  conversationId={conversationId}
                  canDelete={canManageMembers}
                />
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onClose}>
              {t('chat:manageMembers.closeButton')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

ManageMembersDialog.propTypes = {
  conversationId: PropTypes.any.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ManageMembersDialog;
